<script lang="ts">
	import Carousel from '$lib/carousel.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import type { ISbStoryData, StoryblokComponentType } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import type { SbVideo } from '../types';
	import type { ImageBlock } from './imageBlock.svelte';

	interface Props {
		content: ISbStoryData<{
			assets: Array<
				ISbStoryData<
					ImageBlock | SbVideo | StoryblokComponentType<'responsive_mux_video'>
				>['content']
			>;
			mobile_aspect_ratio: '1-1' | '4-5';
		}>['content'];
	}

	let { content }: Props = $props();

	let active = $state(0);

	let embla = $state<EmblaCarouselType>();

	function next() {
		if (embla) {
			embla.canScrollNext() ? embla.scrollNext() : embla.scrollTo(0);
		}
	}

	function previous() {
		if (embla) {
			embla.canScrollPrev() ? embla.scrollPrev() : embla.scrollTo(content.assets.length - 1);
		}
	}

	let firstImageIndex = $derived(content.assets.findIndex((item) => item.component === 'image'));
</script>

<div
	use:editable={content}
	class="group relative max-lg:mt-2 lg:mb-5 lg:ml-5 lg:max-xl:mr-5 xl:w-full"
>
	<div class="max-content-height sticky top-0 overflow-hidden lg:rounded-xl xl:h-full">
		<Carousel
			overflow
			bind:embla
			bind:currentIndex={active}
			autoplay={false}
			containerClass="max-lg:px-5 xl:h-full"
			class={[
				'flex max-xl:gap-x-2 lg:aspect-auto xl:h-full',
				{
					'aspect-square': content.mobile_aspect_ratio === '1-1',
					'aspect-[0.8]': !content.mobile_aspect_ratio || content.mobile_aspect_ratio === '4-5'
				}
			]}
			align="center"
		>
			{#each content.assets as item, index}
				{@const isFirstImage = index === firstImageIndex && item.component === 'image'}
				<div
					class={['flex-none basis-full overflow-hidden max-lg:rounded-lg']}
					{...isFirstImage ? { itemprop: 'image', content: item.asset.filename } : {}}
				>
					<Blok content={item} />
				</div>
			{/each}
		</Carousel>

		<div class="absolute inset-x-4 bottom-4 flex justify-center">
			<div class="flex gap-x-2 rounded-full bg-black/24 p-2 backdrop-blur-md md:hidden">
				{#each content.assets as _, i}
					<!-- svelte-ignore a11y_click_events_have_key_events -->
					<div
						tabindex="-1"
						role="button"
						class={[
							'h-2 w-2 rounded-full',
							{ 'bg-white': i === active, 'bg-white/50': i !== active }
						]}
						onclick={() => embla?.scrollTo(i)}
					></div>
				{/each}
			</div>
			<div class="flex gap-x-2 max-md:hidden">
				{#each content.assets as asset, i}
					<!-- svelte-ignore a11y_click_events_have_key_events -->
					<div
						tabindex="-1"
						role="button"
						onclick={() => embla?.scrollTo(i)}
						class={[
							'relative m-1 h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg outline outline-2 outline-offset-2 transition-[outline] duration-300',
							{
								'outline-white': active === i,
								'opacity-60 outline-white/0 hover:opacity-100': active !== i
							}
						]}
					>
						{#if asset.component === 'mux_video'}
							<Blok content={asset} hideControls />
						{:else}
							<Blok content={{ ...asset, height: 128, width: 128 }} />
						{/if}
					</div>
				{/each}
			</div>
		</div>

		{#if content.assets.length > 3}
			<button
				onclick={previous}
				class="absolute left-4 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-white p-3 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] max-xl:hidden"
			>
				<ArrowLeft class="h-6 w-6" />
			</button>
			<button
				onclick={next}
				class="absolute right-4 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-white p-3 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] max-xl:hidden"
			>
				<ArrowRight class="h-6 w-6" />
			</button>
		{/if}
	</div>
</div>

<style>
	.max-content-height {
		@media (min-width: 1024px) {
			max-height: calc(100vh - var(--full-header-height) - 45px);
		}
	}
</style>
